import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { withTranslation, Trans } from 'react-i18next';

import { makeGetCurrentUserId } from '../selectors/users';
import { makeGetUserRunningTimeEntry, makeGetUserRunningTimeEntryByTaskId, makeGetTimeEntriesByTaskId, makeGetUserTimeEntriesByTaskId } from '../selectors/time-tracking';
import { makeGetFetchedState, makeGetLoadingState } from '../selectors/global';

import { fetchTimeEntries, startTimeEntry, stopTimeEntry, updateTimeEntry } from '../actions/time-entries';
import { openFlyout } from '../actions/flyout';

import { getTimeEntryDuration, getFormattedDuration, groupTimeEntries, getTimeEntriesDuration } from '../utils/time-tracking';
import { createAlert } from '../services/alert-service';

import TaskName from './task-name';
import ProjectName from './project-name';
import Tooltip from './tooltip';
import OverflowTooltip from './overflow-tooltip';
import UserFaces from './user-faces';
import DateTime from 'components/DateTime';

import IconStopwatch from '../icons/icon-stopwatch';
import IconCheck from '../icons/icon-check';
import IconPlay from '../icons/icon-play';
import IconStop from '../icons/icon-stop';
import IconEdit from '../icons/icon-edit';
import IconArrowRight from '../icons/icon-arrow-right';

const UserTooltipElement = ({ id, name, timeGroup, onSeeFullLogClick, t }) => {
    const handleSeeFullLogClick = () => {
        onSeeFullLogClick(id);
    }

    return (
        <div className="popout-inner">
            <div className="popout-head">
                { name.toUpperCase() }: { t('Time Tracking') }
            </div>

            <div className="popout-body">
                <p className="popout-text-content">
                    {
                        t('Time tracked hours', {
                            trackedHours: getFormattedDuration(timeGroup.totalDuration)
                        })
                    }

                    <br />

                    <strong className="clickable text-colored" onClick={handleSeeFullLogClick}>{ t('View Time Log') } <IconArrowRight size="8px" /></strong>
                </p>
            </div>
        </div>
    )
}

class TimeTrackerCta extends PureComponent {
    static defaultProps = {
        fetchTime: true
    };

    state = {
        editing: false,
        elapsedTime: null,
        editValue: null
    };

    handleClick = () => {
        if (this.props.runningTimeEntry) {
            if (this.props.runningTimeEntry.task && this.props.runningTimeEntry.project) {
                this.props.history.push(`/${this.props.runningTimeEntry.project}/task/${this.props.runningTimeEntry.task}`);
            }

            return;
        }
    }

    handleEditClick = () => {
        this.setState({
            editing: true,
            editValue: this.state.elapsedTime
        }, () => {
            if (this.inputField) {
                this.inputField.focus();
            }
        });
    }

    handleEditCompleteClick = () => {
        this.setState({
            editing: false
        });

        const { t } = this.props;

        const matches = this.state.editValue.match(/^(\d+):(\d{1,2}):(\d{1,2})$/);

        if (!matches) {
            createAlert('error', t('The time format is invalid'));
            return;
        }

        updateTimeEntry(this.props.runningTimeEntry.id, {
            start: moment().subtract(matches[1], 'hours').subtract(matches[2], 'minutes').subtract(matches[3], 'seconds').valueOf()
        });

    }

    handleEditValueChange = () => {
        this.setState({
            editValue: this.inputField.value
        });
    }

    handleStartClick = () => {
        this.start();
    }

    handleStopClick = () => {
        this.stop();
    }

    handleSeeFullLogClick = () => {
        this.openFullLog();
    }

    handleSeeUserFullLogClick = userId => {
        this.openFullLog(userId);
    }

    handleLabelClick = () => {
        openFlyout('timeTracking', {
            taskId: this.props.taskId,
            project: this.props.projectId,
            association: 'task'
        });
    }

    openFullLog() {
        if (this.props.runningTimeEntry) {
            openFlyout('timeTracking', {
                taskId: this.props.runningTimeEntry.task,
                project: this.props.runningTimeEntry.project,
                association: 'task'
            });

            return;
        }

        openFlyout('timeTracking', {
            taskId: this.props.taskId,
            project: this.props.projectId,
            association: 'task'
        });
    }

    start() {
        if (this.state.loading) {
            return;
        }

        this.setState({
            loading: true
        });

        if (this.props.runningTimeEntry) {
            stopTimeEntry(this.props.runningTimeEntry.id);
        }

        startTimeEntry({
            task: this.props.taskId,
            project: this.props.projectId,
        }).then(() => {
            this.setState({
                loading: false
            });
        });
    }

    stop() {
        if (this.state.loading) {
            return;
        }

        this.setState({
            loading: true
        });

        stopTimeEntry(this.props.runningTimeEntry.id).then(() => {
            this.setState({
                loading: false,
                elapsedTime: null
            });
        });
    }

    startInterval() {
        this.updateInterval = setInterval(() => {
            this.updateElapsedTime();
        }, 1000);
    }

    updateElapsedTime() {
        const elapsedTime = this.getElapsedTime();

        if (this.state.elapsedTime !== elapsedTime && !this.state.editing) {
            this.setState({ elapsedTime });
        }
    }

    getElapsedTime() {
        return getFormattedDuration(getTimeEntryDuration(this.props.runningTimeEntry));
    }

    stopUpdateInterval() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
            this.updateInterval = null;
        }
    }

    attemptFetchTimeTracking() {
        if (!this.props.taskId || !this.props.fetchTime) {
            return;
        }

        if (!this.props.timeEntriesFetched && !this.props.timeEntriesLoading) {
            fetchTimeEntries({
                taskId: this.props.taskId
            });
        }

    }

    fetchMyRunningTimer() {
        fetchTimeEntries({
            userId: this.props.currentUserId,
            type: 'running'
        });
    }

    componentDidMount() {
        if (this.props.runningTimeEntry) {
            this.startInterval();
        } else {
            this.fetchMyRunningTimer();
        }

        this.attemptFetchTimeTracking();

    }

    componentDidUpdate(prevProps) {
        if (this.props.runningTimeEntry) {
            if (!this.updateInterval) {
                this.startInterval();
            }

            if (!prevProps.runningTimeEntry || this.props.runningTimeEntry.start !== prevProps.runningTimeEntry.start) {
                this.updateElapsedTime();
            }
        } else {
            this.stopUpdateInterval();
        }

        if (prevProps.taskId !== this.props.taskId) {
            this.attemptFetchTimeTracking();
        }
    }

    componentWillUnmount() {
        this.stopUpdateInterval();
    }

    render() {
        const { t } = this.props;

        if (this.props.placeholder) {
            return (
                <div className="time-tracker-cta">
                    <div className="cta-inner">
                        <div className="cta-controls">
                            <div className="control-item">
                                <div className="control-item-inner">
                                    <IconPlay style={{ marginRight: '-3px' }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        }

        if (!this.props.runningTimeEntry && !this.props.taskId) {
            return null;
        }

        if (this.props.iconOnly) {
            return (
                <div className={'time-tracker-cta' + (this.props.runningTimeEntry ? ' started' : '') + (this.props.userTrackedTime ? ' has-tracked-time' : '')}>
                    <div className="cta-inner">
                        <div className="cta-controls">
                            {
                                this.state.elapsedTime || this.props.userTrackedTime ? <span className="cta-control-label" onClick={this.handleLabelClick}>{this.state.elapsedTime || this.props.userTrackedTime}</span> : null
                            }

                            {
                                this.props.runningTimeEntry ? (
                                    <div className="control-item">
                                        <div className="control-item-inner" onClick={this.handleStopClick}>
                                            <IconStop size="8px" />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="control-item">
                                        <Tooltip tooltip="Start tracking time">
                                            <div className="control-item-inner" onClick={this.handleStartClick}>
                                                <IconPlay style={{ marginRight: '-3px' }} />
                                            </div>
                                        </Tooltip>
                                    </div>
                                )
                            }
                        </div>
                    </div>
                </div>
            )
        }

        const usersWithTimes = groupTimeEntries(this.props.timeEntries, 'user');

        return (
            <div className={'time-tracker-cta' + (this.props.runningTimeEntry ? ' started' : '')}>
                <div className="cta-inner">
                    <span className="cta-click-overlay" onClick={this.handleClick} />

                    {
                        this.props.runningTimeEntry ? (
                            <Tooltip
                                tooltip={
                                    <p className="popout-text-content">
                                        <Trans
                                            i18nKey="time-tracking:timerStartedAt"
                                            values={{
                                                startedAt: this.props.runningTimeEntry.start,
                                                elapsedTime: this.state.elapsedTime
                                            }}
                                            components={{
                                                DateTime: <DateTime format="time" />
                                            }}
                                        />

                                        <br />

                                        <strong className="clickable text-colored" onClick={this.handleSeeFullLogClick}>{ t('View Time Log') } <IconArrowRight size="8px" /></strong>
                                    </p>
                                }
                                className="light"
                                placement="top"
                            >
                                <div className="cta-icon" onClick={this.handleSeeFullLogClick}>
                                    <IconStopwatch />
                                </div>
                            </Tooltip>
                        ) : (
                            <div className="cta-icon" onClick={this.handleSeeFullLogClick}>
                                <IconStopwatch />
                            </div>
                        )
                    }

                    <div className="cta-body">
                        {
                            this.state.editing ? (
                                <input type="text" value={this.state.editValue} onChange={this.handleEditValueChange} ref={input => this.inputField = input} />
                            ) : this.props.runningTimeEntry ? (
                                this.state.elapsedTime
                            ) : usersWithTimes.length ? (
                                <UserFaces
                                    users={usersWithTimes}
                                    size="small"
                                    tooltip={true}
                                    tooltipPlacement="top"
                                    maxUsersCount={4}
                                    onClick={this.handleSeeFullLogClick}
                                    tooltipContent={props =>
                                        <UserTooltipElement
                                            id={props.id}
                                            name={props.name}
                                            timeGroup={usersWithTimes.find(group => group.id === props.id)}
                                            onSeeFullLogClick={this.handleSeeUserFullLogClick}
                                            t={t}
                                        />}
                                    />
                            ) : (
                                t('Start timer')
                            )
                        }

                        {
                            !this.props.taskId ? (
                                <OverflowTooltip>
                                    <small>
                                        <TaskName id={this.props.runningTimeEntry.task} />
                                        &nbsp;•&nbsp;
                                        <ProjectName id={this.props.runningTimeEntry.project} />
                                    </small>
                                </OverflowTooltip>
                            ) : null
                        }
                    </div>

                    <div className="cta-controls">
                        {
                            this.state.editing ? (
                                <div className="control-item">
                                    <div className="control-item-inner" onClick={this.handleEditCompleteClick}>
                                        <IconCheck />
                                    </div>
                                </div>
                            ) : this.props.runningTimeEntry ? (
                                <React.Fragment>
                                    <div className="control-item">
                                        <div className="control-item-inner" onClick={this.handleEditClick}>
                                            <IconEdit />
                                        </div>
                                    </div>

                                    <div className="control-item">
                                        <div className="control-item-inner" onClick={this.handleStopClick}>
                                            <IconStop size="8px" />
                                        </div>
                                    </div>
                                </React.Fragment>
                            ) : (
                                <div className="control-item">
                                    <Tooltip tooltip="Start tracking time">
                                        <div className="control-item-inner" onClick={this.handleStartClick}>
                                            <IconPlay style={{ marginRight: '-3px' }} />
                                        </div>
                                    </Tooltip>
                                </div>
                            )
                        }
                    </div>
                </div>
            </div>
        )
    }
}

const makeMapStateToProps = () => {
    const getCurrentUserId = makeGetCurrentUserId();
    const getUserRunningTimeEntry = makeGetUserRunningTimeEntry();
    const getUserRunningTimeEntryByTaskId = makeGetUserRunningTimeEntryByTaskId();
    const getTimeEntriesByTaskId = makeGetTimeEntriesByTaskId();
    const getUserTimeEntriesByTaskId = makeGetUserTimeEntriesByTaskId();
    const getTimeEntriesFetched = makeGetFetchedState();
    const getTimeEntriesLoading = makeGetLoadingState();

    return ({ users, timeTracking }, ownProps) => {
        const currentUserId = getCurrentUserId(users);
        const currentUserTimeEntries = ownProps.taskId && getUserTimeEntriesByTaskId(timeTracking.items, currentUserId, ownProps.taskId);

        return {
            currentUserId,
            timeEntries: ownProps.taskId ? getTimeEntriesByTaskId(timeTracking.items, ownProps.taskId) : [],
            userTrackedTime: currentUserTimeEntries && currentUserTimeEntries.length && getFormattedDuration(getTimeEntriesDuration(currentUserTimeEntries)),
            timeEntriesFetched: ownProps.taskId ? getTimeEntriesFetched(timeTracking.fetchInfo, 'task', ownProps.taskId) : true,
            timeEntriesLoading: ownProps.taskId ? getTimeEntriesLoading(timeTracking.fetchInfo, 'task', ownProps.taskId) : false,
            runningTimeEntry: ownProps.taskId ? getUserRunningTimeEntryByTaskId(timeTracking.items, currentUserId, ownProps.taskId) : getUserRunningTimeEntry(timeTracking.items, currentUserId)

        }
    }
};

export default withRouter(connect(makeMapStateToProps)(withTranslation('time-tracking')(TimeTrackerCta)));
